<template>
  <div class="fence-report-page">
    <!-- Header com Título e Ações -->
    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-1">
      <div>
        <div class="d-flex align-items-center mb-25">
          <b-button
            variant="flat-primary"
            size="sm"
            class="btn-icon mr-50 p-25"
            :to="{ name: 'fence-management' }"
            v-b-tooltip.hover
            :title="$t('Voltar para Gestão de Cercas')"
          >
            <feather-icon icon="ArrowLeftIcon" size="18" />
          </b-button>
          <h3 class="font-weight-bolder text-primary mb-0">
            {{ $t('Relatório de Cercas Virtuais') }}
          </h3>
        </div>
        <p class="text-muted font-small-3 mb-0 ml-2">
          {{ $t('Histórico de eventos de entrada, saída e permanência dos veículos nas cercas.') }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-1 mt-sm-0" style="gap: 8px;">
        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon icon="FilterIcon" size="14" class="mr-50" />
          <span>{{ $t('Filtros') }}</span>
          <b-badge v-if="hasActiveFilters" variant="warning" pill class="ml-25 font-small-1">
            !
          </b-badge>
        </b-button>

        <b-button
          v-if="items.length > 0"
          variant="info"
          size="sm"
          :disabled="isExporting"
          @click="exportAlertsExcel"
          class="d-inline-flex align-items-center"
        >
          <b-spinner v-if="isExporting" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50" />
          <span>{{ $t('Export to Excel') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Painel de Filtros -->
    <transition name="slide-fade">
      <b-card v-if="showFilters" no-body class="shadow-sm mb-1">
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon icon="SlidersIcon" size="16" class="text-primary mr-50" />
            <h6 class="mb-0 font-weight-bolder">{{ $t('Parâmetros de Filtragem') }}</h6>
          </div>
          <div class="d-flex align-items-center" style="gap: 6px;">
            <b-button
              v-if="hasActiveFilters"
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              @click="clearFilters"
              v-b-tooltip.hover
              :title="$t('Limpar Filtros')"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="showFilters = false"
              v-b-tooltip.hover
              :title="$t('Close Filters')"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <b-row class="align-items-end">
            <!-- Data Início -->
            <b-col cols="12" sm="6" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Date Start') }}</label>
              <flat-pickr
                v-model="dateStart"
                class="form-control form-control-sm"
                :placeholder="$t('Selecione data inicial')"
              />
            </b-col>

            <!-- Data Fim -->
            <b-col cols="12" sm="6" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Date End') }}</label>
              <flat-pickr
                v-model="dateEnd"
                class="form-control form-control-sm"
                :placeholder="$t('Selecione data final')"
              />
            </b-col>

            <!-- Veículo / Ativo -->
            <b-col cols="12" sm="6" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Asset') }}</label>
              <vue-autosuggest
                v-model="searchAsset"
                :suggestions="filteredOptionsAsset"
                :input-props="inputPropsAsset"
                :get-suggestion-value="getSuggestionValueAsset"
                @selected="selectHandlerAsset"
                @input="onInputChangeAsset"
              >
                <template slot-scope="{ suggestion }">
                  <div class="p-50 border-bottom">
                    <span class="font-weight-bold d-block">
                      {{ suggestion.item.plate ? suggestion.item.plate : suggestion.item.chassisNumber }}
                    </span>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Grupo de Veículos -->
            <b-col cols="12" sm="6" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Group of Asset') }}</label>
              <vue-autosuggest
                v-model="searchGroup"
                :suggestions="filteredOptionsGroup"
                :input-props="inputPropsGroup"
                :get-suggestion-value="getSuggestionValueGroup"
                @selected="selectHandlerGroup"
                @input="onInputChangeGroup"
              >
                <template slot-scope="{ suggestion }">
                  <div class="p-50 border-bottom">
                    <span class="font-weight-bold d-block">{{ suggestion.item.name }}</span>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Cerca Virtual -->
            <b-col cols="12" sm="6" md="3" class="mb-1 mb-md-0 mt-md-1 mt-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Cerca Virtual') }}</label>
              <vue-autosuggest
                v-model="searchFence"
                :suggestions="filteredOptionsFence"
                :input-props="inputPropsFence"
                :get-suggestion-value="getSuggestionValueFence"
                @selected="selectHandlerFence"
                @input="onInputChangeFence"
              >
                <template slot-scope="{ suggestion }">
                  <div class="p-50 border-bottom">
                    <span class="font-weight-bold d-block">{{ suggestion.item.name }}</span>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>
          </b-row>

          <div class="d-flex justify-content-end mt-1">
            <b-button variant="primary" size="sm" @click="fetchAlerts(1)" class="d-inline-flex align-items-center">
              <feather-icon icon="SearchIcon" size="14" class="mr-50" />
              <span>{{ $t('Search') }}</span>
            </b-button>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Card Principal de Resultados -->
    <b-card no-body class="shadow-sm">
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner variant="primary" class="mr-50" />
        <span class="text-muted font-weight-bold">{{ $t('Carregando eventos...') }}</span>
      </div>

      <div v-else-if="items.length === 0" class="text-center py-4 text-muted">
        <feather-icon icon="CalendarIcon" size="36" class="mb-50 text-muted opacity-50" />
        <h5>{{ $t('No matching records found') }}</h5>
        <small>{{ $t('Ajuste os filtros de data ou veículos para visualizar eventos.') }}</small>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="thead-light">
            <tr>
              <th style="width: 140px;">{{ $t('Horário') }}</th>
              <th style="width: 180px;">{{ $t('Veículo / Placa') }}</th>
              <th>{{ $t('Tipo de Evento') }}</th>
              <th>{{ $t('Cerca') }}</th>
              <th>{{ $t('Informações Adicionais') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(group, gIndex) in items">
              <!-- Linha de Cabeçalho do Dia -->
              <tr :key="'day-' + gIndex" class="day-divider-row bg-light">
                <td colspan="5" class="py-50 px-1 font-weight-bolder text-primary">
                  <feather-icon icon="CalendarIcon" size="14" class="mr-50 text-primary" />
                  <span>{{ group.dayWeek }} — {{ group.date }}</span>
                </td>
              </tr>

              <!-- Eventos do Dia -->
              <tr v-for="(ev, eIndex) in group.hours" :key="'ev-' + gIndex + '-' + eIndex">
                <td class="font-weight-bold text-nowrap">
                  <feather-icon icon="ClockIcon" size="13" class="mr-25 text-muted" />
                  {{ formatTime(ev.datetime) }}
                </td>
                <td class="font-weight-bolder text-dark">
                  <feather-icon icon="TruckIcon" size="13" class="mr-25 text-primary" />
                  {{ ev.plate ? ev.plate : ev.chassisNumber }}
                </td>
                <td>
                  <b-badge variant="light-warning" class="font-small-1">
                    <feather-icon icon="AlertTriangleIcon" size="11" class="mr-25" />
                    {{ ev.eventTypeName }}
                  </b-badge>
                </td>
                <td class="text-muted font-small-2 font-weight-bold">
                  {{ ev.virtualFenceName || '—' }}
                </td>
                <td class="text-muted font-small-2">
                  {{ ev.additionalInfo || '—' }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Rodapé com Paginação -->
      <div v-if="items.length > 0" class="p-1 d-flex flex-column flex-sm-row justify-content-between align-items-center border-top">
        <span class="text-muted font-small-3 mb-1 mb-sm-0">
          {{ $t('Showing') }} {{ (currentPage - 1) * 10 + 1 }}
          {{ $t('to') }} {{ Math.min(currentPage * 10, totalItems) }}
          {{ $t('of') }} {{ totalItems }} {{ $t('entries') }}
        </span>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalItems"
          per-page="10"
          first-number
          last-number
          class="mb-0"
          prev-class="prev-item"
          next-class="next-item"
          @input="onPageChange"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="16" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="16" />
          </template>
        </b-pagination>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { useToast } from 'vue-toastification/composition'

import {
  BCard,
  BCardBody,
  BCardHeader,
  BRow,
  BCol,
  BButton,
  BBadge,
  BSpinner,
  BPagination,
  VBTooltip,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { VueAutosuggest } from 'vue-autosuggest'

import alarmsStoreModule from '@/views/main/alarms/alarmsStoreModule'
import managementStoreModule from '@/views/main/management/managementStoreModule'

export default {
  name: 'FenceReport',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    BPagination,
    flatPickr,
    VueAutosuggest,
  },
  setup(props, { root }) {
    const toast = useToast()

    const ALARMS_APP_STORE_MODULE_NAME = 'app-alarms'
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'

    if (!store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) {
      store.registerModule(ALARMS_APP_STORE_MODULE_NAME, alarmsStoreModule)
    }
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(ALARMS_APP_STORE_MODULE_NAME)
      }
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      }
    })

    const showFilters = ref(true)
    const isLoading = ref(false)
    const isExporting = ref(false)

    // Filtros
    const dateStart = ref('')
    const dateEnd = ref('')
    const searchAsset = ref('')
    const assetId = ref('')
    const searchGroup = ref('')
    const groupId = ref('')
    const searchFence = ref('')
    const virtualFenceId = ref('')

    // Paginação e dados
    const currentPage = ref(1)
    const totalItems = ref(0)
    const items = ref([])

    // Autosuggest inputs
    const filteredOptionsAsset = ref([])
    const inputPropsAsset = computed(() => ({
      id: 'autosuggest-asset',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Pesquisar veículo / placa...'),
    }))

    const filteredOptionsGroup = ref([])
    const inputPropsGroup = computed(() => ({
      id: 'autosuggest-group',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Pesquisar grupo...'),
    }))

    const filteredOptionsFence = ref([])
    const inputPropsFence = computed(() => ({
      id: 'autosuggest-fence',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Pesquisar cerca virtual...'),
    }))

    const hasActiveFilters = computed(() => {
      return dateStart.value || dateEnd.value || assetId.value || groupId.value || virtualFenceId.value
    })

    const getSuggestionValueAsset = suggestion => {
      return suggestion.item.plate ? suggestion.item.plate : suggestion.item.chassisNumber
    }

    const getSuggestionValueGroup = suggestion => {
      return suggestion.item.name
    }

    const getSuggestionValueFence = suggestion => {
      return suggestion.item.name
    }

    const selectHandlerAsset = option => {
      if (option && option.item) {
        searchAsset.value = option.item.plate ? option.item.plate : option.item.chassisNumber
        assetId.value = option.item.id
        filteredOptionsAsset.value = []
      }
    }

    const onInputChangeAsset = text => {
      if (!text || text.length < 2) {
        assetId.value = ''
        filteredOptionsAsset.value = []
        return
      }

      store
        .dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAssets`, {
          q: text,
          page: 1,
          perPage: 6,
        })
        .then(response => {
          filteredOptionsAsset.value = [{ data: response.data.data || [] }]
        })
        .catch(() => {
          filteredOptionsAsset.value = []
        })
    }

    const selectHandlerGroup = option => {
      if (option && option.item) {
        searchGroup.value = option.item.name
        groupId.value = option.item.id
        filteredOptionsGroup.value = []
      }
    }

    const onInputChangeGroup = text => {
      if (!text || text.length < 2) {
        groupId.value = ''
        filteredOptionsGroup.value = []
        return
      }

      store
        .dispatch(`${MANAGEMENT_APP_STORE_MODULE_NAME}/fetchAllGroup`, {
          q: text,
          page: 1,
          perPage: 6,
        })
        .then(response => {
          filteredOptionsGroup.value = [{ data: response.data.data || [] }]
        })
        .catch(() => {
          filteredOptionsGroup.value = []
        })
    }

    const selectHandlerFence = option => {
      if (option && option.item) {
        searchFence.value = option.item.name
        virtualFenceId.value = option.item.id
        filteredOptionsFence.value = []
      }
    }

    const onInputChangeFence = text => {
      if (!text || text.length < 2) {
        virtualFenceId.value = ''
        filteredOptionsFence.value = []
        return
      }

      store
        .dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchFences`, {
          q: text,
          page: 1,
          perPage: 6,
        })
        .then(response => {
          filteredOptionsFence.value = [{ data: response.data.data || [] }]
        })
        .catch(() => {
          filteredOptionsFence.value = []
        })
    }

    const clearFilters = () => {
      dateStart.value = ''
      dateEnd.value = ''
      searchAsset.value = ''
      assetId.value = ''
      searchGroup.value = ''
      groupId.value = ''
      searchFence.value = ''
      virtualFenceId.value = ''
      filteredOptionsAsset.value = []
      filteredOptionsGroup.value = []
      filteredOptionsFence.value = []
    }

    const setHourEnd = date => {
      const dt = new Date(date).toISOString()
      return `${dt.split('T')[0]}T23:59:59.${dt.split('.')[1] || '000Z'}`
    }

    const formatTime = date => {
      return new Date(date).toLocaleTimeString(navigator.language, {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const getDayWeek = dt => {
      const parts = dt.split('/')
      const day = parts[0]
      const month = parts[1]
      const year = parts[2]
      const week = new Date(`${month}/${day}/${year}`).getDay()

      const days = [
        root.$t('Sunday'),
        root.$t('Monday'),
        root.$t('Tuesday'),
        root.$t('Wednesday'),
        root.$t('Thursday'),
        root.$t('Friday'),
        root.$t('Saturday'),
      ]
      return days[week] || ''
    }

    const groupAlarmsByDate = alarms => {
      const groups = alarms.reduce((acc, curr) => {
        const dateStr = new Date(curr.datetime).toLocaleDateString(getIntlLocale())
        if (!acc[dateStr]) acc[dateStr] = []
        acc[dateStr].push(curr)
        return acc
      }, {})

      return Object.keys(groups).map(date => ({
        date,
        dayWeek: getDayWeek(date),
        hours: groups[date],
      }))
    }

    const fetchAlerts = (page = 1) => {
      isLoading.value = true
      currentPage.value = page

      const start = dateStart.value ? new Date(dateStart.value).toISOString() : ''
      const end = dateEnd.value ? setHourEnd(dateEnd.value) : ''
      const alertIds = [9, 10, 11]

      store
        .dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAlerts`, {
          q: '',
          page: currentPage.value,
          assetId: assetId.value,
          alertIds,
          start,
          end,
          groupId: groupId.value,
          virtualFenceId: virtualFenceId.value,
        })
        .then(response => {
          const rawList = response.data.data || []
          items.value = groupAlarmsByDate(rawList)
          totalItems.value = response.data.totalItems || 0
        })
        .catch(() => {
          toast.error(root.$t('Erro ao carregar eventos de cerca virtual.'))
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const exportAlertsExcel = async () => {
      isExporting.value = true
      const start = dateStart.value ? new Date(dateStart.value).toISOString() : ''
      const end = dateEnd.value ? setHourEnd(dateEnd.value) : ''
      const alertIds = [9, 10, 11]

      try {
        const response = await store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/exportAlertsExcel`, {
          q: '',
          page: currentPage.value,
          assetId: assetId.value,
          alertIds,
          start,
          end,
          groupId: groupId.value,
          virtualFenceId: virtualFenceId.value,
        })

        const date = new Date().toLocaleDateString().replace(/\//g, '-')
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `cercas_eventos_${date}.xls`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        toast.success(root.$t('Relatório exportado com sucesso!'))
      } catch (e) {
        toast.error(root.$t('Falha ao exportar relatório.'))
      } finally {
        isExporting.value = false
      }
    }

    const onPageChange = page => {
      fetchAlerts(page)
    }

    onMounted(() => {
      fetchAlerts(1)
    })

    return {
      showFilters,
      isLoading,
      isExporting,
      dateStart,
      dateEnd,
      searchAsset,
      assetId,
      searchGroup,
      groupId,
      searchFence,
      virtualFenceId,
      currentPage,
      totalItems,
      items,
      filteredOptionsAsset,
      inputPropsAsset,
      filteredOptionsGroup,
      inputPropsGroup,
      filteredOptionsFence,
      inputPropsFence,
      hasActiveFilters,
      getSuggestionValueAsset,
      getSuggestionValueGroup,
      getSuggestionValueFence,
      selectHandlerAsset,
      onInputChangeAsset,
      selectHandlerGroup,
      onInputChangeGroup,
      selectHandlerFence,
      onInputChangeFence,
      clearFilters,
      fetchAlerts,
      exportAlertsExcel,
      formatTime,
      onPageChange,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>

<style lang="scss" scoped>
.fence-report-page {
  position: relative;
}

.day-divider-row {
  border-top: 2px solid rgba(115, 103, 240, 0.15);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
